import i18next from 'i18next';
import { Icon } from '@iconify/react';
import homeIcon from '@iconify/icons-material-symbols/home';
import settingsIcon from '@iconify/icons-eva/settings-2-fill';
import connectedTv from '@iconify/icons-material-symbols/connected-tv';
import devicesIcon from '@iconify/icons-material-symbols/devices-fold-outline';
import adminPanelIcon from '@iconify/icons-material-symbols/admin-panel-settings-outline';

import productsConfig from '../../productsController/sidebarConfig';
import { useAuth } from '../../authProvider';
import { licPermissoes } from '../../service/licenciamento/permissoes';

const getIcon = (name) => <Icon icon={name} width={22} height={22} />;

export const useNavConfig = () => {
  const { roles } = useAuth();

  const pricePanel = roles.includes('Price_Panel');
  const ratingSystem = roles.includes('Rating_System');
  const ticketSystem = roles.includes('Ticket_System');
  const pizzaSystem = roles.includes('Pizza_System');
  const anywhere = roles.includes('Anywhere');
  const anywhereAdmin = roles.includes('Anywhere_Admin');
  const lic = licPermissoes(roles);

  const config = [];

  if (pricePanel || ratingSystem || ticketSystem || pizzaSystem || anywhere || anywhereAdmin) {
    config.push({
      title: i18next.t('sidebar.home'),
      path: '/dashboard',
      icon: getIcon(homeIcon),
    });
  }

  // Cada item de produtos fica no nível raiz. O Robot exige LIC_read, não Price_Panel.
  // Nenhum grupo tem página própria, então o título apenas abre o grupo.
  productsConfig().forEach((item) => config.push({ ...item, accessible: false }));

  // `Anywhere` only opens the interface. Reading the data is the backend's `AW_read`, and every
  // mutation keeps its own AW_* permission.
  if (anywhere) {
    config.push({
      title: 'Anywhere',
      accessible: false,
      path: '/anywhere',
      icon: getIcon(connectedTv),
      children: [
        { title: 'Terminais', path: '/anywhere/terminais' },
        { title: 'Programações', path: '/anywhere/programacoes' },
        { title: 'Mídias', path: '/anywhere/midias' },
        { title: 'Banco de imagens', path: '/anywhere/banco-imagens' },
        { title: 'Conteúdo', path: '/anywhere/conteudos' },
        { title: 'Monitoramento', path: '/anywhere/monitoramento' },
      ],
    });
  }

  if (anywhereAdmin) {
    config.push({
      title: 'Anywhere Admin',
      accessible: false,
      path: '/anywhere/admin/clientes',
      icon: getIcon(connectedTv),
      children: [
        { title: 'Clientes', path: '/anywhere/admin/clientes' },
        { title: 'Mídias de todos', path: '/anywhere/admin/midias' },
        { title: 'Banco de imagens', path: '/anywhere/admin/banco-imagens' },
      ],
    });
  }

  // Licenciamento: cada item aparece pela role da chamada principal da tela; o back confere todas.
  const itensCliente = [
    lic.cliente.ativar && { title: 'Ativar máquina', path: '/licenciamento/ativar' },
    lic.cliente.read && { title: 'Dispositivos', path: '/licenciamento/dispositivos' },
    lic.cliente.read && { title: 'Licenças', path: '/licenciamento/licencas' },
  ].filter(Boolean);

  // `LIC_robot_config` sozinha não abre nada (a página exige `LIC_read`): sem item visível não há grupo.
  if (itensCliente.length) {
    config.push({
      title: 'Licenciamento',
      accessible: false,
      path: '/licenciamento',
      icon: getIcon(devicesIcon),
      children: itensCliente,
    });
  }

  if (lic.admin.algum) {
    config.push({
      title: 'Licenciamento Admin',
      accessible: false,
      path: '/admin-licenciamento',
      activeByChildren: true,
      icon: getIcon(adminPanelIcon),
      children: [
        lic.admin.ativar && { title: 'Ativar máquina', path: '/admin-licenciamento/ativar' },
        lic.admin.read && { title: 'Pendentes', path: '/admin-licenciamento/pendentes' },
        lic.admin.read && { title: 'Dispositivos', path: '/admin-licenciamento/dispositivos' },
        lic.admin.read && { title: 'Inventário', path: '/admin-licenciamento/inventario' },
        lic.admin.read && { title: 'Licenças', path: '/admin-licenciamento/licencas' },
        lic.admin.read && { title: 'Vínculo MLQ', path: '/admin-licenciamento/mlq' },
      ].filter(Boolean),
    });
  }

  if (lic.admin.read) {
    config.push({
      title: 'Operações Admin',
      accessible: false,
      path: '/admin-operacoes',
      activeByChildren: true,
      icon: getIcon(settingsIcon),
      children: [
        { title: 'Robot PP', path: '/admin-licenciamento/robot' },
        { title: 'Fila do Hermes', path: '/admin-licenciamento/hermes' },
        { title: 'Configuração', path: '/admin-licenciamento/travas' },
      ],
    });
  }

  return config;
};

export const adminNav = () => [
  {
    title: i18next.t('sidebar.settings'),
    path: '/config',
    icon: getIcon(settingsIcon),
  },
];
